import {Injectable, inject} from '@angular/core';
import {AuthenticationService} from '../authentication.service';
import {iif, merge, Observable, of, switchMap, zip} from 'rxjs';
import {every, first, map, shareReplay} from 'rxjs/operators';
import {UsersService} from '../data/users.service';
import {anyTrue, debugFirst, switchMapIfFalse, switchMapIfTrue} from '../../_utils/operators';
import {Router, UrlTree} from '@angular/router';
import {User} from '../../_data/admin/user';

/**
 * Provides easy methods for components to know what a user is allowed to do.
 *
 * Primarily works as a wrapper to interpret the authentication service and user state to provide answers like "can access this section"
 *
 * The difference can be explained primarily as: The auth service answers what the user IS, this gui permissions service answers what the user can SEE/DO.
 *
 * @see {@link AuthenticationService}
 */
@Injectable({
  providedIn: 'root'
})
export class GuiPermissionsService {


  /** TU Admin, Admins, and Operators */
  readonly canViewAdmin$: Observable<boolean>;

  /** TU Admin, and Operators */
  readonly canViewAdminSearch$: Observable<boolean>;

  /** TU Admin, and Operators */
  readonly canViewAdminSideCards$: Observable<boolean>;

  /** Simple alias for AuthenticationService.isBusiness$ */
  readonly canViewBusiness$: Observable<boolean>;

  /** Simple alias for AuthenticationService.isConsumer$ */
  readonly canViewConsumer$: Observable<boolean>;

  /** TU Admin */
  readonly canCreateCompany$: Observable<boolean>;

  /** TU Admin */
  readonly canEditCompany$: Observable<boolean>;

  /** TU Admin */
  readonly canDeleteCompany$: Observable<boolean>;

  /** TU Admin */
  readonly canAddCompanyAdmins$: Observable<boolean>;

  /** TU Admin */
  readonly canCreateBranches$: Observable<boolean>;

  /**
   * Provides a UrlTree for route-guard redirection.
   * Checks if the user is an admin and if their companyName is not null.  If it is null, the user is forwarded to their home page.
   *
   * @see {@link GuiPermissionsService.homePageRedirectUrlTree$}
   */
  readonly companyAdminRedirectUrlTree$: Observable<UrlTree>;

  /**
   * Provides a UrlTree for route-guard redirection.
   * Will be the user's home page or the "unauthorized" page if there is no home page for some reason.
   *
   * This one should only be used by the companyAdminRedirectUrlTree observable.  Every other potential caller should call companyAdminRedirectUrlTree$ because
   * that one will correctly forward company admins to their company admin page first.
   *
   * @see {@link GuiPermissionsService.companyAdminRedirectUrlTree$}
   */

  private readonly authService = inject(AuthenticationService);
  private readonly usersService = inject(UsersService);
  private readonly router = inject(Router);
  private readonly homePageRedirectUrlTree$: Observable<UrlTree>;

  constructor() {

    this.canViewAdmin$ = this.authService.isTuAdminOrAny(this.authService.isAdmin$, this.authService.isOperator$).pipe(shareReplay(1));
    this.canViewAdminSearch$ = this.authService.isTuAdminOrAny(this.authService.isOperator$).pipe(shareReplay(1));

    //Simple aliases
    this.canViewAdminSideCards$ = this.canViewAdminSearch$;
    this.canViewBusiness$ = this.authService.isBusiness$;
    this.canViewConsumer$ = this.authService.isConsumer$;
    this.canCreateCompany$ = this.authService.isTuAdmin$;
    this.canEditCompany$ = this.authService.isTuAdmin$;
    this.canDeleteCompany$ = this.authService.isTuAdmin$;
    this.canAddCompanyAdmins$ = this.authService.isTuAdmin$;
    this.canCreateBranches$ = this.authService.isTuAdmin$;

    //Build the more complicated observables
    this.homePageRedirectUrlTree$ = this.buildHomePageRedirectUrlTree();
    this.companyAdminRedirectUrlTree$ = this.buildCompanyAdminRedirectUrlTree();

  }

  /** Checks if the logged-in user is assigned to the provided company */
  private isMemberOfCompany(companyName: string): Observable<boolean> {
    return debugFirst(
      `Checking if member of ${companyName}`,
      this.authService.companyName$.pipe(map(myCompanyName => myCompanyName === companyName))
    ).pipe(first());
  }

  /** Checks if the logged-in user is the company admin for the provided company  */
  private isCompanyAdmin(companyName: string): Observable<boolean> {
    //First, check if the user is an admin
    return debugFirst(`Checking if company admin for ${companyName}`, this.authService.isAdmin$.pipe(
      //If they are, return whether or not they are also a member of the provided company, otherwise false
      switchMap(isAdmin => isAdmin ? this.isMemberOfCompany(companyName) : of(false)),
      first()
    ));
  }

  /** @see {@link homePageRedirectUrlTree$} */
  private buildHomePageRedirectUrlTree(): Observable<UrlTree> {
    return this.authService.homePage$.pipe(
      //We have to send them somewhere if they don't have a home page.  Default to not-authorized
      map(homePage => homePage ? homePage : '/not-authorized'),
      //Router that will use this needs it as a UrlTree
      map(homePage => this.router.parseUrl(homePage)),
      shareReplay(1)
    );
  }

  /** @see {@link companyAdminRedirectUrlTree$} */
  private buildCompanyAdminRedirectUrlTree(): Observable<UrlTree> {
    // Send the user to their company admin root page unless that can't be figured out.  If it can't, send them home.
    return this.authService.isAdmin$.pipe(
      switchMap(isAdmin => {
        if(isAdmin) {
          return this.authService.companyName$.pipe(
            switchMap(companyName => iif(() => !!companyName, of(this.router.createUrlTree(['admin', 'companies', companyName, 'details'])), this.homePageRedirectUrlTree$))
          );
        } else {
          return this.homePageRedirectUrlTree$;
        }
      }),
      shareReplay(1)
    );
  }

  /**
   * Helps do a configurable check that's common.
   *
   *  - Can't be self
   *  - TU Admin or any of (if turned on):
   *    - Company admin for same company as target user
   *    - An Operator
   *
   * @param user - The target user
   * @param allowCompanyAdmin - If not TU Admin, can Company Admins do it?
   * @param allowOperator - If not TU Admin, can Operators do it?
   */
  private isNotMeAnd(user: string | User, allowCompanyAdmin: boolean = false, allowOperator: boolean = false) {

    // const isNotMeAndDebugMsg = 'isNotMeAnd ' + JSON.stringify({user, allowCompanyAdmin, allowOperator});

    // These will go into a boolean-or check.  So to skip one that isn't turned on, just have it return false in an observable
    const companyAdmin$ = allowCompanyAdmin ? this.isCompanyAdminForUser(user) : of(false);
    const operator$ = allowOperator ? this.authService.isOperator$ : of(false);

    // Add debug messages that will print on failure, meaning the item that caused this method to claim the action wasn't allowed.
    // companyAdmin$ = companyAdmin$.pipe(tapIf(v => allowCompanyAdmin && !v, () => console.debug('Failed companyAdmin check', isNotMeAndDebugMsg)));
    // operator$ = operator$.pipe(tapIf(v => allowOperator && !v, () => console.debug('Failed operator check', isNotMeAndDebugMsg)));

    // Set up the core observable that will do the heavy lifting, running through the checks
    return merge(
      this.authService.isNotMe(user),
      //Need to use the first() operator because the every() operator waits for everything to be completed (and the authService observables don't usually complete)
      this.authService.isTuAdminOrAny(operator$, companyAdmin$).pipe(first())
    ).pipe(
      //Check that all values (merged observables) are true, converting that to a final true/false (we need all checks to pass)
      every(val => !!val),
      first()
    );

  }

  /**
   * Checks the logged-in user is a company admin and the company is the same as the target user
   */
  private isCompanyAdminForUser(user: string | User): Observable<boolean> {
    return switchMapIfTrue(
      //First, check if we're even a company admin
      this.authService.isAdmin$,
      //If we are, then compare our company name to the user's
      zip(
        this.authService.companyName$,
        this.usersService.getUserDetailsQuietly(user)
      ).pipe(map(([companyName, userDetails]) => companyName === userDetails.company))
    );
  }

  /** TU Admin, Operator, or the company's admin */
  canViewUser(user: string | User): Observable<boolean> {
    //TU Admin or company admin is handled by checking if we can edit the user.  If not, we see if we're an operator
    return switchMapIfFalse(
      this.canEditUser(user),
      this.authService.isOperator$
    ).pipe(first());
  }

  /** TU Admin, Operator, or the company's admin */
  canViewCompany(companyName: string): Observable<boolean> {
    return this.authService.isTuAdminOrAny(this.authService.isOperator$, this.isCompanyAdmin(companyName)).pipe(first());
  }

  /**
   * If companyName is provided: TU Admin or the company's admin.
   * If not: TU Admin
   */
  canAddUser(companyName?: string): Observable<boolean> {
    return debugFirst(
      `Checking if can add user to ${companyName}`,
      //If the user is a TU Admin, it doesn't matter if companyName is provided or not.  If they aren't a TU Admin, then a companyName must be provided and the user must be
      //an admin of that company.
      this.authService.isTuAdminOrAny(companyName ? this.isCompanyAdmin(companyName) : of(false))
    );
  }

  /**
   *  - Cannot be self.
   *  - Is a TuAdmin or: is a company admin and both the logged-in user and target user belong to the same company
   *
   * Emits once
   */
  canEditUser(user: string | User): Observable<boolean> {
    return this.isNotMeAnd(user, true, false);
  }

  /**
   * Same rules as {@link canEditUser()}.
   */
  canDisableUser(user: string | User): Observable<boolean> {
    return this.isNotMeAnd(user, true, false);
  }

  /**
   * Same rules as {@link canEditUser()}.
   */
  canEnableUser(user: string | User): Observable<boolean> {
    return this.isNotMeAnd(user, true, false);
  }

  /**
   * Same rules as {@link canEditUser()}.
   */
  canTerminateUser(user: string | User): Observable<boolean> {
    return this.isNotMeAnd(user, true, false);
  }

  /**
   *  - Cannot be self.
   *  - Is a TuAdmin or either of:
   *      - is a company admin and both the logged-in user and target user belong to the same company
   *      - is an operator
   *
   * Emits once
   */
  canResetPassword(user: string | User): Observable<boolean> {
    return this.isNotMeAnd(user, true, true);
  }

  /**
   * Any of:
   *  - Self
   *  - TU Admin
   *  - Company Admin
   */
  canChangePrimaryMfa(user: string | User): Observable<boolean> {
    return anyTrue(this.authService.isMe(user), this.authService.isTuAdmin$, this.isCompanyAdminForUser(user));
  }

  /** Same rules as {@link canChangePrimaryMfa} */
  canRemoveMfa(user: string | User): Observable<boolean> {
    return this.canChangePrimaryMfa(user);
  }

}
